import { NextSeo } from 'next-seo';
import { useMemo } from 'react';
import Breadcrumb from '@/components/breadcrumb';
import LayoutBase from '@/components/layout-base';
import contentCatalogueUrl from '@/content/content-catalogue-url';
import contentLayoutBaseData from '@/content/content-layout-base-data';
import ContentProductDetailPage from '@/content/content-product-detail-page';
import contentProductDetailUrl from '@/content/content-product-detail-url';
import applicationRuntimeHostname from '@/lib/application-runtime-hostname';
import i18nMessagesLoad from '@/lib/i18n-messages-load';
import { localizationContextFromGetStaticPropsContext } from '@/lib/localization-contenxt';
import revalidateAfter from '@/lib/revalidate-after';
import type { RouterOutput } from '@/lib/trpc';
import createTrpcSSg from '@/lib/trpc-ssg';
import type { NextGetStaticProps, NextPageWithLayout } from '@/pages/_app';
import type { GetStaticPaths } from 'next';

type ProductDetailPageUrlParams = {
  elementName: string;
  productSku: string;
};

type ProductDetail = NonNullable<RouterOutput['product']['detail']>;

type ProductDetailPageProps = {
  localizationContext: {
    currency: string;
    locale: string;
    region: string;
  };
  parentCategories: {
    hash: string;
    name: string;
  }[];
  productDetail: ProductDetail;
};

const ProductDetailPage: NextPageWithLayout<ProductDetailPageProps> = ({
  // localizationContext,
  parentCategories,
  productDetail,
}) => {
  const productHref = useMemo(
    () =>
      `${typeof window !== 'undefined' ? applicationRuntimeHostname(window.location) : ''}${contentProductDetailUrl(
        productDetail.sku,
        productDetail.name,
      )}`,
    [productDetail.name, productDetail.sku],
  );

  return (
    <>
      <NextSeo title={productDetail.name} />

      <main className='content-container'>
        <Breadcrumb className='py-4 print:hidden'>
          {parentCategories.map((category, idx) => (
            <Breadcrumb.Item
              key={category.hash}
              href={contentCatalogueUrl(category.hash, category.name)}
              isCurrenctPage={false}
              position={idx + 1}
            >
              {category.name}
            </Breadcrumb.Item>
          ))}
          <Breadcrumb.Item
            href={contentProductDetailUrl(productDetail.sku, productDetail.name)}
            isCurrenctPage={true}
            position={parentCategories.length + 1}
          >
            {productDetail.name}
          </Breadcrumb.Item>
        </Breadcrumb>

        <ContentProductDetailPage productHref={productHref} {...productDetail} />
      </main>
    </>
  );
};

ProductDetailPage.displayName = 'ProductDetailPage';

ProductDetailPage.getLayout = (page) => {
  return <LayoutBase>{page}</LayoutBase>;
};

export default ProductDetailPage;

// ************************************
// **
// ** getStaticProps
// **
// ************************************

export const getStaticProps: NextGetStaticProps<ProductDetailPageProps, ProductDetailPageUrlParams> = async (
  getStaticPropsContext,
) => {
  if (getStaticPropsContext.params?.productSku == null) return { notFound: true };
  const productSku = getStaticPropsContext.params.productSku;

  const localizationContext = localizationContextFromGetStaticPropsContext(getStaticPropsContext);
  const intlMessages = await i18nMessagesLoad(localizationContext);
  const layoutData = await contentLayoutBaseData(localizationContext);
  const trpc = await createTrpcSSg();

  const mainCategoryHash = await trpc.product.mainCategoryHash.fetch({
    locale: localizationContext.locale,
    region: localizationContext.region,
    sku: productSku,
  });

  const parentCategories =
    mainCategoryHash == null
      ? []
      : ((x) => {
          if (x == null) return [];
          return [...x.parents, { hash: x.hash, name: x.name }];
        })(
          await trpc.catalogue.categoryInfo.fetch({
            categoryHash: mainCategoryHash,
            locale: localizationContext.locale,
            region: localizationContext.region,
          }),
        );

  const productDetail = await trpc.product.detail.fetch({
    currency: localizationContext.currency,
    locale: localizationContext.locale,
    region: localizationContext.region,
    sku: productSku,
  });

  if (productDetail == null) return { notFound: true };

  return {
    props: {
      intlMessages,
      layoutData,
      localizationContext,
      parentCategories,
      productDetail,
    },
    revalidate: revalidateAfter('1h'),
  };
};

// ************************************
// **
// ** getStaticPaths
// **
// ************************************

export const getStaticPaths: GetStaticPaths<ProductDetailPageUrlParams> = async () => {
  const trpc = await createTrpcSSg();
  const pathsParams = await trpc.product.staticPaths.fetch({
    locale: 'cs',
    region: 'CZ',
  });

  return {
    fallback: 'blocking',
    paths: pathsParams.map((params) => ({
      params,
    })),
  };
};
